import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { NAV_GROUPS, applyRoleFilter, applyPermissionFilter, filterGroups, flattenLeaves } from "@/components/layout/navConfig";

/** Jump-to-a-page search in the top bar (it used to sit at the bottom of the left side bar). */
export function TopSearch() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOrgAdmin, seesWholePortfolio } = useAuth();
  const { canView } = usePermissions();
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  const groups = useMemo(
    () => applyPermissionFilter(applyRoleFilter(NAV_GROUPS, { seesWholePortfolio, isOrgAdmin }), canView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [seesWholePortfolio, isOrgAdmin, canView]
  );
  const q = query.trim().toLowerCase();
  const results = useMemo(() => (q ? flattenLeaves(filterGroups(groups, q, projectId), projectId).slice(0, 8) : []), [q, groups, projectId]);

  useEffect(() => { setQuery(""); }, [location.pathname]);
  useEffect(() => {
    if (!q) return undefined;
    const h = (e) => { if (!boxRef.current?.contains(e.target)) setQuery(""); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [q]);

  const go = (to) => { navigate(to); setQuery(""); };

  useEffect(() => {                                   // Ctrl/Cmd + K jumps to the box from anywhere
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); inputRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div ref={boxRef} data-tour="top-search" className="relative hidden w-[240px] shrink-0 md:block xl:w-[280px]">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#C9D2E6]" />
      <input
        ref={inputRef}
        data-testid="sidebar-search-input"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && results[0]) go(results[0].to); if (e.key === "Escape") setQuery(""); }}
        placeholder="Jump to a page..."
        aria-label="Jump to a page"
        className="h-10 w-full rounded-xl border border-white/20 bg-transparent pl-10 pr-20 text-sm text-white placeholder:text-[#C9D2E6] focus:border-white/50 focus:outline-none focus:ring-1 focus:ring-white/40"
      />
      {!query && <kbd className="pointer-events-none absolute right-3.5 top-1/2 hidden -translate-y-1/2 font-sans text-[13px] text-[#C9D2E6] xl:block">Ctrl + K</kbd>}
      {query && <button data-testid="sidebar-search-clear-button" onClick={() => setQuery("")} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#6B7A8D] hover:text-[#D6657F]"><X className="h-3.5 w-3.5" /></button>}
      {q && (
        <div className="absolute right-0 top-full z-50 mt-1.5 max-h-72 w-[320px] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-white/10 bg-[#1F2B47] py-1 shadow-2xl" data-testid="sidebar-search-results">
          {results.length === 0 ? <p data-testid="sidebar-search-empty" className="px-4 py-2 text-xs text-[#8B9BB8]">No pages match "{query}"</p> :
            results.map((r) => <button key={r.to} onClick={() => go(r.to)} className="block w-full px-4 py-2 text-left text-[13px] text-[#E6EBF5]/90 hover:bg-white/10">{r.label}</button>)}
        </div>
      )}
    </div>
  );
}
